import {
    BaseTexture,
    Circle,
    Container,
    DisplayObject,
    Graphics,
    IHitArea,
    ObservablePoint,
    Point,
    Rectangle,
    Sprite,
    TextStyle,
    Texture
} from 'pixi.js';
import {
    ImageConfig,
    IService,
    SlotConfig,
    SpineButtonConfig,
    SpineConfig,
    SystemComponentType
} from '@gongxigames/framework/core/types';
import {CoreDI, CoreSettings} from '@gongxigames/framework/core/configs';
import {canvasUtils} from '@gongxigames/framework/core/utils';
import {builderX, Resources, Component, ComponentProps} from '@gongxigames/framework/core/fragments/BuilderX';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {
    AdvancedText,
    Button, FullScreenSprite,
    RichText,
    RichTextOptions, Spine,
    SpineButton, Text, Animation
} from '@gongxigames/framework/core/fragments/display';
import {LanguageService} from '@gongxigames/framework/core/services/LanguageService';
import {ResourceLoader} from '@gongxigames/framework/core/services/resourceLoader/ResourceLoader';
import {ResolutionManager} from '@gongxigames/framework/core/services/ResolutionManager';
import {ScreenManager} from '@gongxigames/framework/core/services/screenManager/ScreenManager';

@injectable()
export class ObjectBuilder implements IService {
    public static NAME: symbol = CoreDI.ObjectBuilder ;
    public type: SystemComponentType = 'service';
    public static readonly SPRITE: string = 'sprite';
    public static readonly MOVIECLIP: string = 'movieclip';
    public static readonly BUTTON: string = 'button';
    public static readonly IMAGE: string = 'image';
    public static readonly TEXT: string = 'text';
    public static readonly ADVANCED_TEXT: string = 'advanced_text';
    public static readonly GRAPHIC: string = 'graphic';
    public static readonly FRAME_ANIMATION: string = 'frame_animation';
    public static readonly TWEEN_ANIMATION: string = 'tween_animation';
    public static readonly resources: Resources = {game_sprite: {}, shared_sprite: {}};

    public static clearResources(): void {
        ObjectBuilder.resources.game_sprite = {};
        ObjectBuilder.resources.shared_sprite = {};
    }

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.ResourceLoader)
    private _loader!: ResourceLoader;

    @inject(CoreDI.ResolutionManager)
    private _resolutionManager!: ResolutionManager;

    @inject(CoreDI.ScreenManager)
    private _screenManager!: ScreenManager;

    @inject(CoreDI.Settings)
    private _settings!: CoreSettings;

    public init(): void {
        this.setContext();
    }

    public destroy(): void {
        ObjectBuilder.clearResources();
    }

    public setContext(): void {
        builderX.setContext({
            resolution: this.getTextureResolution(),
            getLocalization: (name: string) => {
                return this._languageService.get(name);
            },
            getPlatform: () => {
                return this._settings.get('platform');
            },
            getTexture: (name) => {
                const texture = this._loader.getTexture(name);

                if (!texture) {
                    consoleLogger.warn(`❗🖼️ Can't find texture: '${name}'!!!`);
                }

                return texture;
            },
            getLoader: () => {
                return this._loader;
            },
            getLanguageService: () => {
                return this._languageService;
            },
            updateResources: (resources: Resources) => {
                ObjectBuilder.resources.game_sprite = resources.game_sprite;
                ObjectBuilder.resources.shared_sprite = resources.shared_sprite;
            }
        });
    }

    public buildImage(conf: ImageConfig): Sprite {
        const sprite: Sprite = new Sprite(this._loader.getTexture(conf.src));

        this.initDisplayObjectParams(sprite, conf);

        if (conf.xAnchor) {
            sprite.anchor.x = conf.xAnchor;
        }

        if (conf.yAnchor) {
            sprite.anchor.y = conf.yAnchor;
        }

        return sprite;
    }

    public updateImage(sprite: Sprite, conf: ImageConfig): Sprite {
        this.initDisplayObjectParams(sprite, conf);

        if (conf.xAnchor) {
            sprite.anchor.x = conf.xAnchor;
        }

        if (conf.yAnchor) {
            sprite.anchor.y = conf.yAnchor;
        }

        if (conf.src) {
            sprite.texture = this._loader.getTexture(conf.src);
        }

        return sprite;
    }

    public buildGraphicImage(conf: any): Sprite {
        let cornerRadius = 0;
        let rectWidth = 1;
        let rectHeight = 1;

        if (conf.radius) {
            cornerRadius = conf.radius = conf.radius / 2;
            rectWidth = conf.width;
            rectHeight = conf.height;
        }

        const canvas = canvasUtils.createCanvas(rectWidth, rectHeight);
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = conf.color;

        if (cornerRadius > 0) {
            canvasUtils.drawRoundRect(ctx, 0, 0, rectWidth, rectHeight, cornerRadius);
        } else {
            ctx.fillRect(0, 0, 1, 1);
        }

        const sprite = new Sprite(new Texture(BaseTexture.from((canvas))));

        if (conf.alpha != null) {
            sprite.alpha = conf.alpha;
        }

        this.initDisplayObjectParams(sprite, conf);

        return sprite;
    }

    /**
     * @deprecated Use buildRichText instead
     */
    public buildAdvancedText(conf: {text: string; style?: TextStyle;}): AdvancedText {
        return new AdvancedText(this, conf.text, conf.style, this.getTextureResolution());
    }

    public buildRichText(options: RichTextOptions): RichText {
        return new RichText({
            ... options,
            resourceLoader: this._loader,
            languageService: this._languageService,
            resolution: this.getTextureResolution(),
            lang: this._settings.get('lang')
        });
    }

    public buildText(conf: any): Sprite {
        const style = new TextStyle(conf.style);
        const text = new Text(conf.text, style);

        text.resolution = this.getTextureResolution();

        this.initDisplayObjectParams(text, conf);

        if (conf.xAnchor) {
            text.anchor.x = conf.xAnchor;
        }

        if (conf.yAnchor) {
            text.anchor.y = conf.yAnchor;
        }

        if (conf.localeTextID && this._languageService.get(conf.localeTextID)) {
            text.text = this._languageService.get(conf.localeTextID);
        }

        if (conf.maxWidth) {
            text.maxWidth = conf.maxWidth;

            if (!conf.xAnchor) {
                text.anchor.x = 0.5;
                text.x += conf.maxWidth / 2;
            }
        }

        return text;
    }

    /**
     *
     * @param {string} text text to draw
     * @param {string} textSpriteName name of the sprite. That sprite contains characters you need for text
     * @param {number} space by default is 1/10 of image's width
     * @returns {Sprite}
     */
    public buildTextSprite(text: string, textSpriteName: string, space?: number): Sprite {
        const sprite = new Sprite();
        let x = 0;

        for (const c of text) {
            const displayObject = this.buildDisplayObjectByName(textSpriteName, c);

            displayObject.x = x;
            x += displayObject.width;

            if (space === undefined) {
                space = displayObject.width / 10;
            }

            x += space;

            sprite.addChild(displayObject);
        }

        return sprite;
    }

    public getDisplayObjectConfigByName(
        rootSprite: string,
        treeName?: string | number,
        prefix?: string
    ): Readonly<Component<ComponentProps>> | undefined {
        const {game_sprite, shared_sprite} = ObjectBuilder.resources;
        let data = game_sprite[rootSprite] || shared_sprite[rootSprite];

        if (treeName !== undefined) {
            data = data.findByNameOrSrc(treeName, prefix) as Readonly<Component<ComponentProps>>;
        }

        return data;
    }

    public buildDisplayObjectByName(spriteName: string): Sprite;
    public buildDisplayObjectByName(rootSprite: string, childNumber: number): Sprite;
    public buildDisplayObjectByName(rootSprite: string, treeName: string, prefix?: string): Sprite;
    public buildDisplayObjectByName(rootSprite: string, treeName?: any, prefix?: any): Sprite {
        const item = this.getDisplayObjectConfigByName(rootSprite, treeName, prefix)!;

        return this.buildDisplayObject(item as Component<ComponentProps>) as Sprite;
    }

    public patchProps<P extends ComponentProps>(props: P, rootSprite: string, treeName?: string, _prefix?: string): this {
        this.getDisplayObjectConfigByName(rootSprite, treeName)!
            .patchProps(props);

        return this;
    }

    public buildButton(conf: any): Button {
        const frames: DisplayObject[] = [];

        for (let i = 0; i < conf.frames.length; i++) {
            frames[i] = this.buildDisplayObject(conf.frames[i]) as Sprite;
        }

        const button: Button = new Button();

        button.setFrames(frames[0], frames[2], frames[1], frames[3]);

        if (conf.hitarea) {
            button.hitArea = this.buildGeom(conf.hitarea);
        }

        this.initDisplayObjectParams(button, conf);

        return button;
    }

    public buildSpineButton(conf: SpineButtonConfig): SpineButton {
        const {animationNames} = conf;
        const spine: Spine = this.buildSpine(conf.spineName);
        const button: SpineButton = new SpineButton(conf.clickCallback);

        if (conf.hitarea) {
            button.hitArea = this.buildGeom(conf.hitarea);
        }

        button.setUpSpine(spine,
            animationNames.hoverStart,
            animationNames.hoverEnd,
            animationNames.click,
            animationNames.idle,
            conf.trackIndex);

        this.initDisplayObjectParams(button, conf);

        return button;
    }

    public buildGeom(conf: any): IHitArea | null {
        if (conf.type === 'circle') {
            return new Circle(conf.x || 0, conf.y || 0, conf.radius);
        } else if (conf.type === 'rectangle' && conf.width !== -1 && conf.height !== -1) {
            return new Rectangle(conf.x || 0, conf.y || 0, conf.width, conf.height);
        }

        return null;
    }

    public buildDisplayObject<P extends ComponentProps>(
        item: Component<P> | Readonly<Component<P>>
    ): Sprite | Animation {
        return builderX.build(item);
    }

    public buildSprite<P extends ComponentProps>(
        item: Component<P> | Readonly<Component<P>>
    ): Sprite {
        return this.buildDisplayObject(item) as Sprite;
    }

    public updateSprite<T extends Sprite|Animation, P extends ComponentProps>(
        element: T,
        item: Component<P> | Readonly<Component<P>>
    ): T {
        return builderX.build(item, {element}) as T;
    }

    public buildFullScreenSprite(textureName: string): FullScreenSprite {
        const texture = this._loader.getTexture(textureName);
        const screenParameters = this._screenManager.getScreenState();

        if (!texture) {
            consoleLogger.error(`Texture '${textureName}' is missing`);
        }

        return new FullScreenSprite(texture, {screenParameters});
    }

    public buildAnimation(conf: any): Animation {
        const anim: Animation = new Animation();

        if (conf.type === ObjectBuilder.FRAME_ANIMATION) {
            this.buildFrameAnimation(conf, anim);
        } else if (conf.type === ObjectBuilder.TWEEN_ANIMATION) {
            this.buildTweenAnimation(conf, anim);
        } else if (conf.type === ObjectBuilder.SPRITE) {
            for (const child of conf.children) {
                if (child.type === ObjectBuilder.FRAME_ANIMATION) {
                    this.buildFrameAnimation(child, anim);
                } else if (child.type === ObjectBuilder.TWEEN_ANIMATION) {
                    this.buildTweenAnimation(child, anim);
                } else {
                    const displayObject = this.buildDisplayObject(child) as Sprite;

                    if (displayObject) {
                        anim.container.addChild(displayObject);
                    }
                }
            }

            this.initDisplayObjectParams(anim.container, conf);
        }

        return anim;
    }

    public buildTweenAnimation(conf: Component<ComponentProps>, element: Animation): Animation {
        return builderX.build(conf, {element, forceKeepElement: true}) as Animation;
    }

    public buildFrameAnimation(conf: Component<ComponentProps>, element: Animation): Animation {
        return builderX.build(conf, {element, forceKeepElement: true}) as Animation;
    }

    public initDisplayObjectParams(object: Container, conf: any): void {
        if (conf instanceof Component) {
            conf = conf.propsToJson();
        }

        if (conf.x) {
            object.x = conf.x;
        }

        if (conf.y) {
            object.y = conf.y;
        }

        if (conf.width) {
            object.width = conf.width;
        }

        if (conf.height) {
            object.height = conf.height;
        }

        if (conf.scaleX) {
            object.scale.x = conf.scaleX;
        }

        if (conf.scaleY) {
            object.scale.y = conf.scaleY;
        }

        if (conf.flipH) {
            object.scale.x *= -1;
            object.x += object.getLocalBounds().width;
        }

        if (conf.flipV) {
            object.scale.y *= -1;
            object.y += object.getLocalBounds().height;
        }

        if (object instanceof Sprite && conf.anchor && conf.anchor.x) {
            object.anchor.x = conf.anchor.x;
        }

        if (object instanceof Sprite && conf.anchor && conf.anchor.y) {
            object.anchor.y = conf.anchor.y;
        }

        if (conf.name) {
            object.name = conf.name;
        }

        if (conf.mask) {
            const rect = new Graphics();

            rect.beginFill(0xffffff);
            rect.drawRect(conf.mask[0], conf.mask[1], conf.mask[2], conf.mask[3]);
            rect.endFill();
            object.mask = rect;
            object.addChild(object.mask);
        }

        if (conf.visible !== undefined) {
            object.visible = conf.visible;
        }
    }

    public buildSpine(name: string): Spine {
        return this._loader.getSpine(name);
    }

    public buildSpineWithConfig(config: SpineConfig): Spine {
        const spine = this.buildSpine(config.name);

        if (config.slots) {
            config.slots.forEach(value => this.setUpSlot(value, spine));
        }

        this.initDisplayObjectParams(spine, config);

        return spine;
    }

    public setUpSlot(slotConfig: SlotConfig, spine: Spine, slotScale?: ObservablePoint | Point): DisplayObject {
        const {name, text, sprite, richText} = slotConfig;
        const slot = spine.skeleton.findSlot(name);
        const {currentSprite} = slot;
        let displayObject;

        if (sprite) {
            const {rootSprite, treeName, prefix} = sprite;

            displayObject = this.buildDisplayObjectByName(rootSprite, treeName, prefix);

            this.initDisplayObjectParams(displayObject, slotConfig);
        } else if (text) {  // deprecated
            if (text.id) {
                text.text = this._languageService.get(text.id);
            }

            displayObject = this.buildAdvancedText({text: text.text!, style: text.style});
            this.initDisplayObjectParams(displayObject, slotConfig);
            displayObject.maxWidth = text.maxWidth;
        } else if (richText) {
            if (this._languageService.get(richText.id) !== 'undefined') {
                richText.blendMode = slot.blendMode;
                displayObject = this.buildRichText(richText);
                this.initDisplayObjectParams(displayObject, slotConfig);
            } else {
                displayObject = currentSprite;
            }
        }

        if (!displayObject) {
            throw new Error(`Cant build display object [${name}]`);
        }

        if (displayObject !== currentSprite) {
            currentSprite.removeChildren();
            currentSprite.texture = Texture.EMPTY;

            if (slotScale) {
                currentSprite.scale = slotScale;
            } else {
                currentSprite.scale.set(1, -1);
            }

            currentSprite.addChild(displayObject);
        }

        return displayObject;
    }

    private getTextureResolution(): number {
        return this._screenManager.defaultViewportResolution;
    }
}
